import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ProjectScope } from '@/lib/schemas';

/**
 * Hook to determine if a deliverable's scope should show a portal prompt.
 *
 * Derives:
 * - isExternal: whether the scope is external (scope_type_slug !== 'self')
 * - portalExists: whether a portal already exists for this scope's organization
 *
 * Returns typed state for use in DeliverableScopePortalPrompt.
 */
export function useDeliverableScopePortal(projectId: string | undefined, scope: ProjectScope | undefined) {
  const { data: projectShare, isLoading, error } = useQuery({
    queryKey: ['project-share', projectId],
    queryFn: () => api.projectShare.getForProject(projectId!),
    enabled: !!projectId && !!scope,
    staleTime: 30_000, // 30s cache
  });

  const isExternal = scope?.scope_type_slug !== 'self';
  const portalExists = isExternal && projectShare?.view
    ? projectShare.view.organization_id === scope?.organization_id
    : false;

  const orgName = scope?.organization_name || scope?.scope_type_name || 'This Organization';

  return {
    isExternal,
    portalExists,
    orgName,
    isLoading,
    error: error as Error | null,
  };
}
